import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PAYMENT_RETURN_PLACEHOLDER, type PaymentConnectionSettings, type PaymentProvider, type PaymentReturnSettings } from '@kontor/shared';
import { api, ApiError } from '../../api';

const errorText = (e: unknown, fallback: string): string => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

/** Webhook-Adresse zum Kopieren, mit Hinweis, wo sie hingehört */
function WebhookBox({ settings, where }: { settings: PaymentConnectionSettings; where: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="card">
      <h2>Webhook</h2>
      <p className="small">Damit Wirkia erfährt, dass der Kunde bezahlt hat, schickt {where} eine Meldung an diese Adresse:</p>
      <input className="input" readOnly value={settings.webhookUrl} aria-label="Webhook-Adresse" onFocus={(e) => e.target.select()} />
      <div className="form-actions">
        <button
          className="btn btn--secondary btn--sm"
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(settings.webhookUrl);
              setCopied(true);
            } catch {
              setCopied(false);
            }
          }}
        >
          {copied ? 'Kopiert' : 'Adresse kopieren'}
        </button>
      </div>
      {!settings.webhookReady && <p className="alert alert--warn small">Der Webhook ist noch nicht eingetragen. Ohne ihn müssen Sie Zahlungen von Hand auf der Rechnung erfassen.</p>}
    </div>
  );
}

/** Stripe des Mandanten (KON-189): eingeschränkter Schlüssel und Signaturgeheimnis des Webhooks, in zwei Schritten, weil die Webhook-Adresse erst nach dem ersten Speichern feststeht */
export function StripeIntegration() {
  return <PaymentForm provider="stripe" />;
}
export function PaypalIntegration() {
  return <PaymentForm provider="paypal" />;
}

function PaymentForm({ provider }: { provider: PaymentProvider }) {
  const client = useQueryClient();
  const current = useQuery({ queryKey: ['integrations', provider], queryFn: async () => (await api<{ data: PaymentConnectionSettings | null }>('GET', `/integrations/${provider}`)).data });
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [environment, setEnvironment] = useState<'sandbox' | 'live'>('sandbox');
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const [webhookId, setWebhookId] = useState('');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const done = (text: string) => {
    client.invalidateQueries({ queryKey: ['integrations'] });
    setSecretKey('');
    setWebhookSecret('');
    setSecret('');
    setWebhookId('');
    setMessage({ ok: true, text });
  };
  const save = useMutation({
    mutationFn: async () => {
      const body =
        provider === 'stripe'
          ? { ...(secretKey ? { secretKey } : {}), ...(webhookSecret ? { webhookSecret } : {}) }
          : { ...(clientId ? { clientId } : {}), ...(secret ? { secret } : {}), ...(webhookId ? { webhookId } : {}), ...(!current.data || clientId || secret ? { environment } : {}) };
      return (await api<{ data: PaymentConnectionSettings }>('PUT', `/integrations/${provider}`, body)).data;
    },
    onSuccess: (s) => done(s.webhookReady ? 'Gespeichert. Die Verbindung steht, und der Webhook ist eingetragen.' : 'Gespeichert. Tragen Sie als Nächstes den Webhook ein (siehe unten).'),
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Speichern hat nicht geklappt.') }),
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/integrations/${provider}`),
    onSuccess: () => done('Die Verbindung ist getrennt, die gespeicherten Zugangsdaten sind gelöscht.'),
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Trennen hat nicht geklappt.') }),
  });

  if (current.isLoading) return <p className="muted">Wird geladen …</p>;
  const saved = current.data;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    save.mutate();
  };

  return (
    <>
      {saved?.status === 'error' && saved.lastError && <div className="alert alert--error" role="alert">Zuletzt hat es nicht geklappt: {saved.lastError}</div>}
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      {saved && <p className="small">Verbunden mit <strong>{saved.label}</strong> ({saved.live ? 'Live, echtes Geld' : 'Test, kein echtes Geld'}).</p>}

      <form className="card" onSubmit={submit} noValidate>
        <h2>{saved ? 'Zugangsdaten ändern' : 'Schritt 1: Zugangsdaten'}</h2>
        {provider === 'stripe' ? (
          <>
            <ol className="small muted">
              <li>Öffnen Sie im Stripe-Dashboard Entwickler → API-Schlüssel und legen Sie einen eingeschränkten Schlüssel an.</li>
              <li>Geben Sie ihm nur das Recht „Checkout Sessions: Schreiben“. Mehr braucht Wirkia nicht.</li>
              <li>Fügen Sie den Schlüssel (beginnt mit rk_live_ oder rk_test_) hier ein. Er wird verschlüsselt gespeichert und nie wieder angezeigt.</li>
            </ol>
            <label className="field"><span>Eingeschränkter Schlüssel {saved ? '' : <abbr title="Pflichtfeld">*</abbr>}</span><input className="input" type="password" value={secretKey} onChange={(e) => setSecretKey(e.target.value)} placeholder={saved ? 'unverändert, zum Ändern neuen eintragen' : 'rk_live_…'} autoComplete="off" /></label>
            {saved && (
              <label className="field"><span>Signaturgeheimnis des Webhooks {saved.webhookReady ? '(eingetragen)' : ''}</span><input className="input" type="password" value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)} placeholder={saved.webhookReady ? 'unverändert' : 'whsec_…'} autoComplete="off" /></label>
            )}
          </>
        ) : (
          <>
            <ol className="small muted">
              <li>Öffnen Sie im PayPal Developer Dashboard (developer.paypal.com) Apps und Anmeldedaten und legen Sie eine App an.</li>
              <li>Kopieren Sie Client-ID und Secret der App, für „Live“ oder „Sandbox“ (Test).</li>
            </ol>
            <div className="form-grid">
              <label className="field">
                <span>Umgebung</span>
                <select className="input" value={environment} onChange={(e) => setEnvironment(e.target.value as 'sandbox' | 'live')}>
                  <option value="sandbox">Test (Sandbox)</option>
                  <option value="live">Live (echtes Geld)</option>
                </select>
              </label>
              <label className="field"><span>Client-ID {saved ? '' : <abbr title="Pflichtfeld">*</abbr>}</span><input className="input" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder={saved ? 'unverändert' : ''} autoComplete="off" /></label>
              <label className="field"><span>Secret {saved ? '' : <abbr title="Pflichtfeld">*</abbr>}</span><input className="input" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={saved ? 'unverändert' : ''} autoComplete="off" /></label>
              {saved && <label className="field"><span>Webhook-ID {saved.webhookReady ? '(eingetragen)' : ''}</span><input className="input" value={webhookId} onChange={(e) => setWebhookId(e.target.value)} placeholder={saved.webhookReady ? 'unverändert' : 'z. B. 8PT597110X687430L'} autoComplete="off" /></label>}
            </div>
          </>
        )}
        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird geprüft …' : saved ? 'Speichern' : 'Prüfen und speichern'}</button>
          {saved && <button className="btn btn--secondary" type="button" disabled={remove.isPending} onClick={() => { if (window.confirm('Verbindung trennen? Bestehende Zahlungslinks funktionieren danach nicht mehr.')) remove.mutate(); }}>Trennen</button>}
        </div>
      </form>

      {saved && (
        <>
          <WebhookBox settings={saved} where={provider === 'stripe' ? 'Stripe' : 'PayPal'} />
          <div className="card">
            <h2>Schritt 2: Webhook beim Dienst anlegen</h2>
            {provider === 'stripe' ? (
              <ol className="small muted">
                <li>Stripe-Dashboard → Entwickler → Webhooks → Endpunkt hinzufügen.</li>
                <li>Tragen Sie die Adresse von oben ein und wählen Sie die Ereignisse „checkout.session.completed“ und „checkout.session.async_payment_succeeded“.</li>
                <li>Kopieren Sie danach das Signiergeheimnis (beginnt mit whsec_) und tragen Sie es oben ein.</li>
              </ol>
            ) : (
              <ol className="small muted">
                <li>PayPal Developer Dashboard → Ihre App → Webhooks → Webhook hinzufügen.</li>
                <li>Tragen Sie die Adresse von oben ein und wählen Sie die Ereignisse „Checkout order approved“ und „Payment capture completed“.</li>
                <li>Kopieren Sie danach die Webhook-ID und tragen Sie sie oben ein.</li>
              </ol>
            )}
          </div>
        </>
      )}
      <PaymentReturnCard />
    </>
  );
}

/**
 * Wohin der Kunde nach dem Bezahlen kommt. Der Mandant mit eigener Webseite trägt hier seine Seiten ein, die Einstellung gilt für Stripe und PayPal. Ohne Angabe landet der Kunde auf der
 * neutralen Seite von Wirkia.
 */
function PaymentReturnCard() {
  const client = useQueryClient();
  const current = useQuery({ queryKey: ['integrations', 'payment-return'], queryFn: async () => (await api<{ data: PaymentReturnSettings }>('GET', '/integrations/payment-return')).data });
  const [successUrl, setSuccessUrl] = useState<string | null>(null);
  const [cancelUrl, setCancelUrl] = useState<string | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: async (v: { success: string; cancel: string }) => (await api<{ data: PaymentReturnSettings }>('PUT', '/integrations/payment-return', { successUrl: v.success.trim() || null, cancelUrl: v.cancel.trim() || null })).data,
    onSuccess: (r) => {
      client.setQueryData(['integrations', 'payment-return'], r);
      setSuccessUrl(null);
      setCancelUrl(null);
      setErrors({});
      setMessage({ ok: true, text: r.successUrl || r.cancelUrl ? 'Gespeichert. Neue Zahlungen führen auf Ihre Seiten.' : 'Gespeichert. Kunden landen auf den Seiten von Wirkia.' });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setMessage(null);
      } else setMessage({ ok: false, text: errorText(e, 'Das Speichern hat nicht geklappt.') });
    },
  });

  if (!current.data) return null;
  const c = current.data;
  const success = successUrl ?? c.successUrl ?? '';
  const cancel = cancelUrl ?? c.cancelUrl ?? '';
  const changed = success !== (c.successUrl ?? '') || cancel !== (c.cancelUrl ?? '');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setErrors({});
    save.mutate({ success, cancel });
  };

  return (
    <form className="card" onSubmit={submit} noValidate>
      <h2>Nach der Zahlung</h2>
      <p className="small muted">
        Hier legen Sie fest, wohin Ihre Kunden kommen, wenn sie eine Rechnung über den QR-Code oder den Zahlungslink bezahlt oder abgebrochen haben. Das gilt für Stripe und PayPal. Haben Sie eine eigene Webseite,
        tragen Sie dort Ihre Dankesseite ein. Ohne Angabe sehen Ihre Kunden eine neutrale Seite von Wirkia.
      </p>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      <label className="field">
        <span>Seite nach erfolgreicher Zahlung</span>
        <input className="input" type="url" inputMode="url" value={success} placeholder={c.defaultSuccessUrl} onChange={(e) => setSuccessUrl(e.target.value)} aria-invalid={!!errors.successUrl} autoComplete="off" />
        {errors.successUrl && <span className="field__error" role="alert">{errors.successUrl}</span>}
        <span className="muted small">Zum Beispiel https://www.beispiel.de/danke. Leer lassen für die Seite von Wirkia.</span>
      </label>
      <label className="field">
        <span>Seite nach abgebrochener Zahlung</span>
        <input className="input" type="url" inputMode="url" value={cancel} placeholder={c.defaultCancelUrl} onChange={(e) => setCancelUrl(e.target.value)} aria-invalid={!!errors.cancelUrl} autoComplete="off" />
        {errors.cancelUrl && <span className="field__error" role="alert">{errors.cancelUrl}</span>}
        <span className="muted small">Hierhin kommt, wer die Bezahlseite verlässt, ohne zu zahlen.</span>
      </label>
      <p className="small muted">
        In der Adresse können Sie <code>{PAYMENT_RETURN_PLACEHOLDER}</code> verwenden. Wirkia setzt dort die Rechnungsnummer ein, zum Beispiel https://www.beispiel.de/danke?rechnung={PAYMENT_RETURN_PLACEHOLDER}. Die Adresse muss mit https:// beginnen. Fehlerseiten
        (ungültiger Link, technischer Fehler) bleiben die von Wirkia.
      </p>
      <div className="form-actions">
        <button className="btn btn--primary" type="submit" disabled={save.isPending || !changed}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>
        {(c.successUrl || c.cancelUrl) && (
          <button className="btn btn--secondary" type="button" disabled={save.isPending} onClick={() => { setMessage(null); setErrors({}); save.mutate({ success: '', cancel: '' }); }}>Auf die Seiten von Wirkia zurücksetzen</button>
        )}
      </div>
    </form>
  );
}
